import { Modal } from "../modal/base_modal";

/** Модальное окно быстрых ответов для тикетов */
export class QuickRepliesModal extends Modal {
  constructor() {
    super({ className: "quick-replies-modal" });
    this.replies = [];
    this.editingReply = null;
    this.searchQuery = "";
  }

  /** Строит HTML-содержимое модального окна
   * @returns {string} HTML-строка с содержимым модального окна
   */
  buildContent() {
    return `
      <div class="quick-replies-header">
        <h3>Шаблоны</h3>
      </div>
      <div class="quick-replies-body">
        <div class="quick-replies-search">
          <input type="text" id="quick-replies-search" placeholder="Поиск..." />
        </div>
        <div class="quick-replies-list" id="quick-replies-list"></div>
        <div class="quick-replies-empty" id="quick-replies-empty" style="display: none;">
          Нет шаблонов
        </div>
      </div>
      <div class="quick-replies-footer">
        <button type="button" class="btn-add" id="add-quick-reply-btn">Добавить</button>
        <button type="button" class="btn-close">Закрыть</button>
      </div>
    `;
  }

  /** Строит HTML формы редактирования/создания быстрого ответа
   * @returns {string} HTML-строка с формой
   */
  buildFormContent() {
    const reply = this.editingReply;
    return `
      <div class="quick-replies-header">
        <h3>${reply ? "Редактировать" : "Добавить"} шаблон</h3>
      </div>
      <div class="quick-replies-body">
        <textarea
          class="quick-reply-textarea"
          id="quick-reply-content"
          placeholder="Введите текст шаблона..."
          maxlength="1000"
        >${reply ? reply.content : ""}</textarea>
      </div>
      <div class="quick-replies-footer">
        <button type="button" class="btn-cancel">Отмена</button>
        <button type="button" class="btn-save">Сохранить</button>
      </div>
    `;
  }

  /** Загружает список быстрых ответов с сервера
   * @returns {Promise<void>}
   */
  async loadReplies() {
    try {
      const response = await fetch("/quick_replies", {
        method: "GET",
        headers: { "Accept": "application/json" }
      });

      if (!response.ok) throw new Error("Не удалось загрузить быстрые ответы");

      this.replies = await response.json();
      this.renderReplies();
    } catch (error) {
      toastr.error(error.message || "Не удалось загрузить быстрые ответы");
    }
  }

  /** Отображает список быстрых ответов
   * @returns {void}
   */
  renderReplies() {
    if (!this.modal) return;

    const list = this.modal.querySelector("#quick-replies-list");
    const empty = this.modal.querySelector("#quick-replies-empty");
    if (!list) return;

    list.innerHTML = "";

    const filtered = this.replies.filter(r =>
      r.content.toLowerCase().includes(this.searchQuery.toLowerCase())
    );

    if (filtered.length === 0) {
      if (empty) empty.style.display = "block";
      return;
    }
    if (empty) empty.style.display = "none";

    filtered.forEach(reply => {
      const item = document.createElement("div");
      item.className = "quick-reply-item";
      item.dataset.replyId = reply.id;

      const content = document.createElement("div");
      content.className = "quick-reply-item__content";
      content.textContent = reply.content;

      const actions = document.createElement("div");
      actions.className = "quick-reply-item__actions";

      const editBtn = document.createElement("button");
      editBtn.type = "button";
      editBtn.className = "quick-reply-item__edit";
      editBtn.innerHTML = "&#9998;";
      editBtn.title = "Редактировать";

      const deleteBtn = document.createElement("button");
      deleteBtn.type = "button";
      deleteBtn.className = "quick-reply-item__delete";
      deleteBtn.innerHTML = "&#10005;";
      deleteBtn.title = "Удалить";

      actions.appendChild(editBtn);
      actions.appendChild(deleteBtn);
      item.appendChild(content);
      item.appendChild(actions);
      list.appendChild(item);
    });
  }

  /** Переключает модальное окно в режим формы
   * @param {Object|null} reply - Редактируемый быстрый ответ или null для создания
   * @returns {void}
   */
  showForm(reply = null) {
    this.editingReply = reply;
    const modalContent = this.modal.querySelector(".modal-content");
    if (!modalContent) return;

    modalContent.innerHTML = this.buildFormContent();
    this.bindFormEvents();

    const textarea = this.modal.querySelector("#quick-reply-content");
    if (textarea) textarea.focus();
  }

  /** Возвращает модальное окно в режим списка
   * @returns {void}
   */
  async hideForm() {
    this.editingReply = null;
    const modalContent = this.modal.querySelector(".modal-content");
    if (!modalContent) return;

    modalContent.innerHTML = this.buildContent();
    this.renderReplies();
    this.bindListEvents();
    await this.loadReplies();
  }

  /** Сохраняет быстрый ответ (создание или обновление)
   * @returns {Promise<void>}
   */
  async saveReply() {
    const textarea = this.modal.querySelector("#quick-reply-content");
    const content = textarea ? textarea.value.trim() : "";

    if (!content) {
      toastr.error("Введите текст быстрого ответа");
      return;
    }

    const id = this.editingReply ? this.editingReply.id : null;
    const method = id ? "PATCH" : "POST";
    const url = id ? `/quick_replies/${id}` : "/quick_replies";

    try {
      const response = await fetch(url, {
        method: method,
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name=csrf-token]").content
        },
        body: JSON.stringify({ quick_reply: { content: content } })
      });

      const data = await response.json();

      if (!response.ok) {
        const errors = data.errors;
        if (errors && errors.content) {
          toastr.error(errors.content.join(", "));
        } else {
          toastr.error("Не удалось сохранить быстрый ответ");
        }
        return;
      }

      toastr.success(id ? "Быстрый ответ обновлён" : "Быстрый ответ создан");
      this.hideForm();
    } catch (error) {
      toastr.error("Не удалось сохранить быстрый ответ");
    }
  }

  /** Удаляет быстрый ответ
   * @param {number} id - ID быстрого ответа
   * @returns {Promise<void>}
   */
  async deleteReply(id) {
    if (!confirm("Удалить быстрый ответ?")) return;

    try {
      const response = await fetch(`/quick_replies/${id}`, {
        method: "DELETE",
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name=csrf-token]").content
        }
      });

      if (!response.ok) throw new Error("Не удалось удалить быстрый ответ");

      toastr.success("Быстрый ответ удалён");
      await this.loadReplies();
    } catch (error) {
      toastr.error(error.message || "Не удалось удалить быстрый ответ");
    }
  }

  /** Вставляет текст быстрого ответа в textarea тикета
   * @param {string} content - Текст быстрого ответа
   * @returns {void}
   */
  insertReply(content) {
    const textarea = document.getElementById("content");
    if (!textarea) return;

    const current = textarea.value.trim();
    textarea.value = current ? current + "\n" + content : content;
    this.close();
  }

  /** Навешивает обработчики событий для режима списка
   * @returns {void}
   */
  bindListEvents() {
    const list = this.modal.querySelector("#quick-replies-list");
    const searchInput = this.modal.querySelector("#quick-replies-search");
    const addBtn = this.modal.querySelector("#add-quick-reply-btn");
    const closeBtn = this.modal.querySelector(".btn-close");

    if (list) {
      list.addEventListener("click", e => {
        const item = e.target.closest(".quick-reply-item");
        if (!item) return;

        const replyId = Number(item.dataset.replyId);
        const reply = this.replies.find(r => r.id === replyId);
        if (!reply) return;

        if (e.target.closest(".quick-reply-item__edit")) {
          this.showForm(reply);
        } else if (e.target.closest(".quick-reply-item__delete")) {
          this.deleteReply(replyId);
        } else if (e.target.closest(".quick-reply-item__content")) {
          this.insertReply(reply.content);
        }
      });
    }

    if (searchInput) {
      searchInput.addEventListener("input", e => {
        this.searchQuery = e.target.value.trim();
        this.renderReplies();
      });
    }

    if (addBtn) {
      addBtn.addEventListener("click", () => this.showForm(null));
    }

    if (closeBtn) {
      closeBtn.addEventListener("click", () => this.close());
    }
  }

  /** Навешивает обработчики событий для режима формы
   * @returns {void}
   */
  bindFormEvents() {
    const cancelBtn = this.modal.querySelector(".btn-cancel");
    const saveBtn = this.modal.querySelector(".btn-save");

    if (cancelBtn) {
      cancelBtn.addEventListener("click", () => this.hideForm());
    }

    if (saveBtn) {
      saveBtn.addEventListener("click", () => this.saveReply());
    }
  }

  /** Настраивает обработчики событий модального окна
   * @param {Object} params - Параметры настройки
   * @param {HTMLElement} params.modal - DOM-элемент модального окна
   * @returns {void}
   */
  setupHandlers({ modal }) {
    this.modal = modal;
    this.renderReplies();
    this.bindListEvents();
  }

  /** Отображает модальное окно быстрых ответов
   * @returns {Promise<void>}
   */
  async show() {
    await this.loadReplies();

    return this.open({
      content: this.buildContent(),
      onInit: ({ modal }) => this.setupHandlers({ modal })
    });
  }
}

/** Делегирование событий на document для открытия модалки */
document.addEventListener("click", function(e) {
  const openBtn = e.target.closest("#open-templates-btn");
  if (openBtn) {
    e.preventDefault();
    const modal = new QuickRepliesModal();
    modal.show();
  }
});
